import {LoyaltyCardObjective, LoyaltyCardObjectiveType, LoyaltyCardPoint} from "../LoyaltyCard";
import {LoyaltyCardAnimation} from "../LoyaltyCardAnimation";
import {FileManagerFile} from "@/core/interfaces/FileManagerFile";
import {Organization} from "@/core/interfaces/organization/hds/Organization";
import Product from "../../../product/interfaces/Product";

export const LOYALTY_CARD_ASPECT_RATIO = 8.5 / 5.5;

/**
 * LoyaltyCard interface.
 *
 * @property {number} id - The loyalty card id.
 * @property {string} name - The loyalty card name.
 * @property {string} description - The loyalty card description.
 * @property {string} formatted_start_date - The loyalty card start date.
 * @property {string} formatted_end_date - The loyalty card end date.
 * @property {string} period - The loyalty card active period.
 * @property {Organization} organization - The loyalty card organization.
 * @property {FileManagerFile} file - The loyalty card file.
 * @property {LoyaltyCardObjective[]} objectives - The loyalty card objectives.
 * @property {number} point_rewards_count - The number of objective rewards with point.
 * @property {number} product_rewards_count - The number of objective rewards with products.
 */
export interface LoyaltyCard {
    id: number;
    name: string;
    description: string;
    formatted_start_date: string;
    formatted_end_date: string;
    period: string;
    organization: Organization;
    file: FileManagerFile;
    objectives: LoyaltyCardObjective[];
    point?: LoyaltyCardPoint;
    point_rewards_count: number;
    product_rewards_count: number;
    analysis?: LoyaltyCardAnalysis;
    /** The loyalty card membership. */
    membership?: LoyaltyCardMembership;
}

/**
 * LoyaltyCardReward interface.
 *
 * @property {string} teaser - The reward teaser.
 */
export interface LoyaltyCardReward {
    teaser: string;
}

/**
 * LoyaltyCardMembership
 *
 * @property {number} id - The membership id.
 * @property {number} point_balance - The membership point balance.
 * @property {number} unit_balance - The unit balance.
 * @property {LoyaltyCard} loyalty_card - The loyalty card.
 * @property {object} formatted - The formatted values.
 * @property {LoyaltyCardMembershipReward} rewards - The membership rewards
 */
export interface LoyaltyCardMembership {
    id: number;
    point_balance: number;
    unit_balance: number;
    loyalty_card: LoyaltyCard & { organization: Organization & {name: string} };
    formatted: {
        created_at: string;
        expired_at: string;
        point_balance: string;
        unit_balance: string;
    },
    /** The membership objectives with their current progress (served by the detail endpoint). */
    objectives?: LoyaltyCardMembershipObjectiveProgress[];
    rewards: LoyaltyCardMembershipReward;
}

/**
 * LoyaltyCardMembershipObjectiveProgress interface.
 *
 * A membership objective with its live progress toward the threshold.
 *
 * @property {number} id - The membership objective id.
 * @property {number} value - The current progress (stamps / points earned).
 * @property {string|null} reached_at - When the objective was reached, null while in progress.
 * @property {object} objective - The card objective definition (holds the threshold + reward).
 */
export interface LoyaltyCardMembershipObjectiveProgress {
    id: number;
    value: number;
    reached_at: string | null;
    objective: {
        id: number;
        /** The threshold to reach the reward. */
        value: number;
        /** The unit the threshold counts: products or euros. */
        type?: LoyaltyCardObjectiveType;
        reward?: LoyaltyCardReward & { value?: number };
    };
}

/**
 * LoyaltyCardMembershipReward interface.
 *
 * @property {number} points - The point rewards value.
 * @property {number} discounts - The discount reward value.
 * @property {number} products - The products reward value.
 */
export interface LoyaltyCardMembershipReward {
    points: number;
    discounts: number;
    products: number;
}

/**
 * A delivered reward row for the "rewards delivered" listing.
 *
 * @property {number} id - The reward id.
 * @property {string|null} customer - The customer name.
 * @property {string|null} date - The formatted delivery date.
 * @property {string} label - The offered product name or reward description.
 * @property {number|null} value - The monetary value in euros, when applicable.
 */
export interface LoyaltyCardRewardItem {
    id: number;
    customer: string | null;
    date: string | null;
    label: string;
    value: number | null;
}

/**
 * LoyaltyCardAnalysisSummary interface.
 *
 * @property {LoyaltyCardAnalysisTotalSummary} total - The total analysis value.
 * @property {LoyaltyCardAnalysisCumulativeSummary} cumulative - The cumulated values.
 */
export interface HealthcenterLoyaltyCardAnalysisSummary {
    total: LoyaltyCardAnalysisTotalSummary;
    cumulative: LoyaltyCardAnalysisCumulativeSummary;
}

export interface GroupingLoyaltyCardAnalysisSummary {
    total: {
        membership: number;
    },
    average: {
        membership: number;
    }
}

/**
 * Loyalty card analysis.
 *
 * The optional members are served by the enriched HDS analysis endpoint
 * (dashboard, Phase 1 backend). The dashboard consumes them with graceful
 * degradation: when a field is absent, the matching KPI/chart renders a
 * "coming soon" state rather than a misleading zero.
 */
export interface LoyaltyCardAnalysis {
    total: {
        point_balance: number;
        credited: number;
        debited: number;
        /** Total unit (product/stamp) balance across memberships. */
        unit_balance: number;
        /** Revenue generated by the program over its tracked range (CA). */
        revenue?: number;
    },
    count: {
        membership: number;
        male: number;
        female: number;
        /** Memberships one product/stamp away from their reward. */
        near_threshold?: number;
    },
    healthcenter_average: {
        cart: number;
    },
    average: {
        cart_price: {
            current: number;
            previous: number;
        };
        cart: number;
        age: number;
        /** Average number of days between two purchases. */
        purchase_frequency_days?: number;
    },
    /** Rewards delivered summary (count + monetary value offered). */
    rewards?: {
        count: number;
        value: number;
    },
    /** Cards opened per month, ordered oldest to newest. */
    monthly_openings?: LoyaltyCardMonthlyOpening[];
    /** Age pyramid buckets split by gender. */
    age_pyramid?: LoyaltyCardAgeBucket[];
    /** Distribution of cards by progression (fill level). */
    progression?: LoyaltyCardProgressionBucket[];
}

/**
 * A single month data-point of card openings.
 *
 * @property {string} label - Short month label, e.g. "févr.".
 * @property {number} count - Number of cards opened during the month.
 */
export interface LoyaltyCardMonthlyOpening {
    label: string;
    count: number;
}

/**
 * An age-pyramid bucket split by gender.
 *
 * @property {string} range - Age range label, e.g. "51-65".
 * @property {number} male - Male members in the range.
 * @property {number} female - Female members in the range.
 */
export interface LoyaltyCardAgeBucket {
    range: string;
    male: number;
    female: number;
}

/**
 * A progression (fill-level) bucket.
 *
 * @property {string} range - Fill-level label, e.g. "6-8 produits".
 * @property {number} count - Number of cards in the bucket.
 */
export interface LoyaltyCardProgressionBucket {
    range: string;
    count: number;
}

/**
 * LoyaltyCardAnalysisTotalSummary interface.
 *
 * @property {number} membership - Total membership count.
 * @property {number} point_balance - Total point balance.
 * @property {number} unit_balance - The total unit balance.
 * @property {LoyaltyCardAnalysisRewardsSummary} rewards - The total rewards.
 */
export interface LoyaltyCardAnalysisTotalSummary {
    membership: number;
    point_balance: number;
    unit_balance: number;
    rewards: LoyaltyCardAnalysisRewardsSummary
}

/**
 * LoyaltyCardAnalysisCumulativeSummary interface.
 *
 * @property {number} point - The cumulative rewarded point.
 * @property {number} unit - The cumulative unit balance.
 */
export interface LoyaltyCardAnalysisCumulativeSummary {
    point: number;
    unit: number;
}

/**
 * LoyaltyCardAnalysisRewardsSummary interface.
 *
 * @property {number} points - The total point reward generated.
 * @property {number} discounts - The total discounts reward generated.
 * @property {number} products - The total products reward generated.
 */
export interface LoyaltyCardAnalysisRewardsSummary {
    points: number;
    discounts: number;
    products: number;
}

/**
 * LoyaltyCardMembershipCountStatistic interface.
 *
 * @property {number} total - The total membership.
 * @property {number} male - The total male membership.
 * @property {number} female - The total female membership.
 */
export interface LoyaltyCardMembershipCountStatistic {
    total: number;
    male: number;
    female: number;
}

/**
 * LoyaltyCardMembershipAverageStatistic interface.
 *
 * @property {number} age - The memberships age average.
 * @property {}
 */
export interface LoyaltyCardMembershipAverageStatistic {
    age: number;
    price: LoyaltyCardMembershipAveragePriceStatistic;
}

/**
 * LoyaltyCardMembershipAveragePriceStatistic interface.
 *
 * @property {number} month - The average cart price for the current 30days range.
 * @property {number} last_month - The average cart price for the last 30 days range
 */
export interface LoyaltyCardMembershipAveragePriceStatistic {
    month: number;
    last_month: number;
}

/**
 * LoyaltyCardMembershipItemStatistic interface.
 *
 * @property {LoyaltyCardMembershipItemCustomerStatistic} customer - The membership customer.
 * @property {LoyaltyCardMembershipItemTotalStatistic} total - The membership computed total value.
 */
export interface LoyaltyCardMembershipItemStatistic {
    customer: LoyaltyCardMembershipItemCustomerStatistic;
    total: LoyaltyCardMembershipItemTotalStatistic;
    /** The average statistic values. */
    average: LoyaltyCardMembershipItemAverageStatistic;

    statistics: {
        last_purchase: string | null,
        reward: LoyaltyCardMembershipReward;
        /** Average number of days between two purchases. */
        frequency_days?: number | null;
        /** Number of rewards obtained by the membership. */
        rewards_count?: number;
    }
    /** Formatted card creation date. */
    created_at?: string;
    /** Current card fill level toward the reward. */
    fill?: {
        current: number;
        threshold: number;
        /** Whether any open objective is one unit away from its reward. */
        near_threshold?: boolean;
    };
}

/**
 * LoyaltyCardMembershipItemCustomerStatistic interface.
 *
 * @property {number} id - The customer id.
 * @property {string} name - The customer name.
 * @property  {number} membership_id - The customer membership id.
 */
export interface LoyaltyCardMembershipItemCustomerStatistic {
    id: number;
    name: string;
    /** The customer age, null when the birth date is unknown. */
    age?: number | null;
    membership_id: number;
}

export interface LoyaltyCardMembershipItemAverageStatistic {
    /** The average revenue. */
    revenue: number;
    /** The average quantity. */
    quantity: number;
}

/**
 * LoyaltyCardMembershipItemTotalStatistic interface.
 *
 * @property {number} revenue - The total revenue for a membership.
 * @property {number} quantity - The total quantity for a membership.
 * @property {number} balance - The total point balance for a membership.
 */
export interface LoyaltyCardMembershipItemTotalStatistic {
    revenue: number;
    quantity: number;
    point_balance: number;
    unit_balance: number;
    value: number;
}

/**
 * LoyaltyCardMembershipTransaction interface.
 *
 * @property {number} id - The transaction id.
 * @property {number} value - The transaction value.
 * @property {number} balance - The balance when the transaction as occured
 * @property {string} sign - The transaction sign.
 * @property {string} title - The transaction title.
 * @property {string} description - The transaction description.
 * @property {object} formatted - The formatted values.
 * @property {LoyaltyCardMembershipTransactionPurchase[]} [purchases] - The transaction purchased.
 * @property {Product} [product] - The product transaction.
 * @property {"product"|"point"} reward_type - The reward type for the transaction.
 */
export interface LoyaltyCardMembershipTransaction {
    id: number;
    /** What the entry is (morph alias); absent on an HDS serving none. */
    type?: "loyaltyCardMembershipObjective"
        | "loyaltyCardMembershipObjectiveProgress"
        | "loyaltyCardMembershipReward"
        | "loyaltyCardMembershipCredit"
        | "loyaltyCardMembershipAdjustment";
    value: number;
    balance: number;
    sign: string;
    title: string;
    description: string;
    formatted: {
        value: string;
        balance: string;
        /** On a points card, the objective fill after a sale that earned no point ("4 €/10 €"). */
        objective_progress?: string | null;
        created_at: string;
    }
    purchases?: LoyaltyCardMembershipTransactionPurchase[];
    product?: Product;
    reward_type?: "product" | "point";
}

/**
 * LoyaltyCardMembershipTransactionPurchase interface.
 *
 * @property {number} id - The purchase id.
 * @property {number} quantity - The purchase quantity.
 * @property {number} price - The purchase price
 * @property {Product} product - The purchased product.
 * @property {LoyaltyCardAnimation} [animation] - The animation linked to the purchase.
 * @property {object} formatted - The formatted values.
 */
export interface LoyaltyCardMembershipTransactionPurchase {
    id: number;
    quantity: number;
    price: number;
    product: Product;
    animation?: LoyaltyCardAnimation;
    formatted: {
        price: string;
    }
}

export enum LoyaltyCartMembershipTransactionType {
    REWARD = "reward",
    PURCHASE = "purchase",
    CREDIT = "credit"
}

/**
 * LoyaltyCardProductStatistic interface.
 *
 * @property {Product} product - The product information.
 * @property {LoyaltyCardProductTotalStatistic} total - The product computed total value.
 */
export interface LoyaltyCardProductStatistic {
    product: Product;
    total: LoyaltyCardProductTotalStatistic;
}

/**
 * LoyaltyCardProductTotalStatistic interface.
 */
export interface LoyaltyCardProductTotalStatistic {
    /** The total revenue of the product. */
    revenue: number;
    /** The total quantity sold of the product. */
    quantity: number;
    /** The total of membership who bought the product. */
    membership_count: number;
    /** The last sale date of the product. */
    last_sale: string;
    /** The total of sale of the product. */
    sale_count: number;
}
